import React, { useEffect, useState } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Chart } from "react-chartjs-2";
import { TreemapController, TreemapElement } from "chartjs-chart-treemap";

// Register required Chart.js components including TreemapController
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend,
  TreemapController,
  TreemapElement
);

const baseDarkRed = "#D3001A"; // Dark red for the largest segment

const generateLighterShades = (count) => {
  const lightnessLevels = [40, 45, 50, 55, 60, 65, 70]; // Lighter red shades
  return Array.from({ length: count }, (_, i) => `hsl(355, 85%, ${lightnessLevels[i % lightnessLevels.length]}%)`);
};

// Same color palette as before
const colors = [
  "#D3001A", // Red
  "#FFB500", // Yellow-Orange
  "#007AFF", // Blue
  "#34C759", // Green
  "#FF3B30", // Bright Red
  "#FF9500", // Orange
  "#AF52DE", // Purple
  "#5AC8FA", // Light Blue
  "#5856D6", // Dark Blue
  "#FF2D55", // Pink-Red
  "#4CD964", // Light Green
  "#8E8E93"  // Gray 
];

const EmployeesByDepartmentChart = ({ departments }) => {
  const [chartData, setChartData] = useState(null);
  
  useEffect(() => {
    if (departments.length === 0) return;
    
    const MIN_SIZE = 70;
    
    // Prepare data for Chart.js
    const data = departments.map(d => ({
      department: d.name, 
      value: Math.max(d.count, MIN_SIZE),
      actualCount: d.count,
      id: d.id_
    }));
    
    setChartData({
      datasets: [{
        tree: data,
        key: "value",
        labels: {
          display: true,
          align: 'center',
          position: 'middle',
          formatter: (ctx) => {
            // First try to truncate department name if it's too long
            const dept = ctx.raw._data.department;
            const count = ctx.raw._data.actualCount;
            const truncatedDept = dept.length > 15 ? dept.substring(0, 12) + '...' : dept;
            return [truncatedDept, count];
          },
          font: {
            size: 11,
            weight: 'bold'
          },
          color: '#FFFFFF',
          overflow: 'fit'
        },
        backgroundColor: (ctx) => {
          return colors[ctx.dataIndex % colors.length];
        },
        spacing: 2,
        borderWidth: 1,
        borderColor: "#fff"
      }]
    });
  }, [departments]);

  // Handle chart element click to open employees page with department filter
  const handleClick = (event, elements) => {
    if (elements.length > 0) {
      const clickedElement = elements[0];
      const id = clickedElement.element.$context.raw._data.id;
      // Open in new tab with department parameter
      window.open(`/employees?department=${id}`, '_blank');
    }
  };

  if (departments.length === 0 || !chartData) return null;

  const options = {
    plugins: {
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            // Full department name always visible in tooltip
            return `${context.raw._data.department}: ${context.raw._data.actualCount}`;
          }
        }
      },
      legend: {
        display: false
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    caretSize: 0, // Remove caret from tooltip
    caretPadding: 10,
    onClick: handleClick, // Add click handler to chart options
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "10px",
        width: "100%",
        // height: "350px",
        margin: "auto",
      }}
    >
      {chartData && (
        <Chart
          type="treemap"
          data={chartData}
          options={options}
          height={250}
          width={"100%"}
        />
      )}
    </div>
  );
};

export default EmployeesByDepartmentChart;
